import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
/* ==========================================================================
   THE CASCADE ORDER, WRITTEN DOWN (T-062)
   ==========================================================================

   Every stylesheet the application loads, in the order it loads, in one place.

   It was not in one place before. `style.css` @imported three sheets from its
   own tail — at line 20,015, which is invalid CSS and worked only because
   webpack is more forgiving than the spec — and `admin/style.css` @imported
   five more from its head, two of which had a rival copy of the same name one
   directory up. Both sets shipped. Which one painted the sidebar was decided by
   the order of two lines here, and nothing said so.

   The order below reproduces exactly what the browser saw before, so nothing
   changes appearance. What changes is that a person can now read it.

   ⚠️ ORDER IS BEHAVIOUR IN THIS FILE. `admin/sidebar.css` sets the sidebar's
   text to white and `assets/css/sidebar.css` sets it to grey; both load, and the
   second one wins. Reordering these lines recolours the sidebar. There is a test
   in src/deadWeight.test.js that fails if the order changes. */

import './assets/css/style.css'
import './assets/css/sidebar.css'

import './assets/css/admin/scrollbar.css'
import './assets/css/admin/sidebar.css'
import './assets/css/admin/progress.css'
import './assets/css/admin/modal.css'
import './assets/css/admin/dropdownmenu.css'
import './assets/css/admin/style.css'

// After the template sheets, so the tokens win where they overlap (T-019).
import './assets/css/tokens.css'

// Points the template's theme-blind classes at those tokens, for the twenty
// screens not yet rewritten on the component library. After tokens, because it
// uses them; after the template sheets, because it overrides them. Both, so
// this position is a requirement rather than a preference.
import './assets/css/legacy-bridge.css'

// The component library's own styles, after the tokens it is built on (T-021).
import './ui/ui.css'

// The glass material, applied to the content rather than the frame. After BOTH
// ui.css and legacy-bridge.css, because it re-points the surfaces each of them
// sets: the library paints .ui-stat with --surface-raised and the bridge paints
// .card with --legacy-surface, and this is the sheet that makes them
// translucent. Position is a requirement, not a preference — loaded before
// either one the interface is opaque again, and nothing errors to say so.
import './ui/glass.css'

// Bengali typography, scoped to :lang(bn) so it costs nothing in English
// (T-024). Last, so it can relax the fixed widths the sheets above set.
import './assets/css/bengali.css'

import 'react-toastify/dist/ReactToastify.css';

import store from './redux/store/store'
import { Provider } from 'react-redux'
import { initTheme } from './theme/theme'
import { initDensity } from './theme/density'
// The dictionaries are chunks now, not static imports (T-044), so the active
// language has to be fetched before the first paint. Rendering first and
// filling in after would show a frame of raw key names — "counter.tryNew" where
// a button label belongs — which reads as a broken build.
import { initI18n } from './i18n'

// Before render, not inside a component. Applying the theme after first paint
// gives a flash of the wrong colours — brief, but on a dark-mode machine that
// is a white flash in a dim room, which is the thing dark mode exists to avoid.
initTheme();
// Same reason as the theme: applying it after first paint means the first frame
// is drawn at the wrong row height and everything jumps once (T-059).
initDensity();

const root = ReactDOM.createRoot(document.getElementById('root'));

/**
 * Render once the language is in memory.
 *
 * `.finally`, not `.then`: a dictionary that fails to fetch — a bad deploy, a
 * dropped connection on a shop PC — must still leave a usable application.
 * i18next falls back to the key names, which is ugly, but a salesperson can
 * still find the Save button. A blank screen means they cannot take the order.
 */
initI18n().finally(() => {
  root.render(
    <React.StrictMode>
      <Provider store={store}>
          <App />
      </Provider>
    </React.StrictMode>
  );
});

